import React, { useCallback, useRef, useState } from "react";
import { useContext } from "react";
import { useEffect } from "react";
import { useUser } from "../../providers/UserProvider";
import { GetUserFriends, toggleFollowUser } from "../../service/userApi";

const FriendsContext = React.createContext(null);

export const FriendsProvider = ({ children }) => {
  const [friends, setFriends] = useState([]);
  const [error, setError] = useState([]);
  const { user } = useUser();
  // The account the provider is currently for. A friends list that arrives
  // after a sign-out or an account switch belongs to someone else and is
  // dropped (code review SEPARATION-7).
  const currentUserIdRef = useRef(user?._id ?? null);
  currentUserIdRef.current = user?._id ?? null;
  const handleGetUserFriends = useCallback(
    async (userId) => {
      try {
        const userFriendsFromClient = await GetUserFriends(userId);
        if (String(currentUserIdRef.current) !== String(userId)) return;
        if (userFriendsFromClient) {
          const unique = Array.from(
            new Map(
              userFriendsFromClient.map((friend) => [
                typeof friend.user_id === "object"
                  ? friend.user_id._id || friend.user_id.toString()
                  : friend.user_id?.toString(),
                friend,
              ])
            ).values()
          );
          return setFriends(unique);
        }
      } catch (error) {
        if (typeof error === "string") return setError(error);
      }
    },
    [user]
  );
  // Taps on the same profile while a request is in flight are dropped.
  const inFlight = useRef(new Set());
  const handleFollowToggle = async (viewedUserId, follow) => {
    if (!user || inFlight.current.has(viewedUserId)) return;
    inFlight.current.add(viewedUserId);
    try {
      await toggleFollowUser(user._id, viewedUserId, follow);
      handleGetUserFriends(user._id);
    } catch (error) {
      console.error("Error toggling follow:", error);
    } finally {
      inFlight.current.delete(viewedUserId);
    }
  };

  useEffect(() => {
    // Start every account from an empty list: the previous account's friends
    // must never show in the next one's session, not even until its own
    // fetch lands (and forever if that fetch fails). (SEPARATION-7)
    setFriends([]);
    if (user) {
      handleGetUserFriends(user._id);
    }
    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id]);
  // Assuming you pass `viewedUserId` into the FriendsProvider

  return (
    <FriendsContext.Provider value={{ friends, handleFollowToggle }}>
      {children}
    </FriendsContext.Provider>
  );
};

export const useFriends = () => {
  const context = useContext(FriendsContext);
  if (!context)
    throw new Error("useFriends must be used within a FriendsProvider");
  return context;
};
